import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { StatisticsRow } from '../../types/supplierStatistics';
import { money, number, percent } from './StatisticsTable';

export const retailColor = '#127d76';
export const onlineColor = '#2563eb';

/** Revenue shares are only meaningful when both channels are known and non-negative. */
export default function StatisticsChannels({
  row,
  compact = false,
}: {
  row: StatisticsRow;
  compact?: boolean;
}) {
  const total = row.retailRevenue + row.onlineRevenue;
  const shareKnown =
    Number.isFinite(row.retailRevenue) &&
    Number.isFinite(row.onlineRevenue) &&
    Number.isFinite(total) &&
    total > 0 &&
    row.retailRevenue >= 0 &&
    row.onlineRevenue >= 0;
  return (
    <Box role="group" aria-label="Försäljning per kanal">
      {!compact && (
        <Typography fontWeight={700} sx={{ mb: 2 }}>
          Försäljning per kanal
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 2,
        }}
      >
        {[
          {
            label: 'Butik²',
            revenue: row.retailRevenue,
            units: row.retailUnitsSold,
            unitLabel: 'nettorader',
            color: retailColor,
          },
          {
            label: 'Online',
            revenue: row.onlineRevenue,
            units: row.onlineUnitsSold,
            unitLabel: 'beställda enheter',
            color: onlineColor,
          },
        ].map((channel) => (
          <Box key={channel.label}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: channel.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {channel.label}
              </Typography>
            </Stack>
            <Typography
              fontWeight={700}
              variant={compact ? 'body2' : 'h6'}
              sx={{ mt: 0.5 }}
            >
              {money(channel.revenue)}
            </Typography>
            {!compact && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {number(channel.units)} {channel.unitLabel}
                {shareKnown &&
                  ` · ${percent((channel.revenue / total) * 100)} av omsättningen`}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      {!compact && shareKnown && (
        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            height: 6,
            borderRadius: 2,
            overflow: 'hidden',
            mt: 2,
            bgcolor: onlineColor,
          }}
        >
          <Box
            sx={{
              width: `${(row.retailRevenue / total) * 100}%`,
              bgcolor: retailColor,
            }}
          />
        </Box>
      )}
    </Box>
  );
}
